/**
 * ============================================================================
 * THEME ENGINE CLASS
 * ============================================================================
 * 
 * Main class to manage theme application and persistence
 */

import { generateFullThemeFromUserInput } from './theme-generator';
import { generateWhitelabelOverrides, generateUtilityClasses } from './css-templates';
import { darkenColor, withOpacity } from './color-utils';

export class ThemeEngine {
  constructor() {
    this.currentTheme = null;
    this.storageKey = 'brandme_theme';
    this.styleTagId = 'brandme-dynamic-theme';
  }

  init() {
    // Check if user is whitelabel
    let isWhitelabel = false;
    try {
      // Try to get user data from local storage
      const userData = localStorage.getItem('userData');
      if (userData) {
        const user = JSON.parse(userData);
        isWhitelabel = user?.main_group?.name === 'whitelabel';
      }
    } catch (e) {
      console.warn('Could not check whitelabel status:', e);
    }
    
    const savedTheme = localStorage.getItem(this.storageKey);
    if (savedTheme) {
      try {
        const theme = JSON.parse(savedTheme);
        this.applyTheme(theme, isWhitelabel);
      } catch (e) {
        console.error('Error loading saved theme:', e);
      }
    } else {
      // not saved theme found, apply default
      try {
        const defaultTheme = generateFullThemeFromUserInput({
          primary: undefined,
          textOnPrimary: undefined,
          headerBg: 'linear-gradient(120deg ,#6947bc, #993e90)',
        });
        this.applyTheme(defaultTheme, false);
      } catch (e) {
        console.warn('Could not apply default theme:', e);
      }
    }
  }

  applyTheme(theme, isWhitelabel = false) {
    // Remove old style tag if exists
    const oldStyle = document.getElementById(this.styleTagId);
    if (oldStyle) {
      oldStyle.remove();
    }
    
    // Create new <style> tag with all CSS variables AND Bootstrap overrides
    const styleTag = document.createElement('style');
    styleTag.id = this.styleTagId;
    
    // Generate CSS string from theme object, EXCLUDING null/undefined values
    // This ensures CSS variable fallbacks work properly
    const cssVariables = Object.entries(theme)
      .filter(([, value]) => value !== null && value !== undefined && value !== 'null')
      .map(([key, value]) => `  ${key}: ${value};`)
      .join('\n');
    
    // Extract primary color for Bootstrap overrides (only used if whitelabel)
    const primaryColor = theme['--brand-primary'] || theme['--primary'] || '#7367f0';
    const primaryHover = theme['--btn-primary-bg-hover'] || darkenColor(primaryColor, 10);
    const primaryShadow = theme['--btn-primary-shadow'] || `0 2px 8px ${withOpacity(primaryColor, 0.25)}`;
    const primaryShadowHover = theme['--btn-primary-shadow-hover'] || `0 4px 12px ${withOpacity(darkenColor(primaryColor, 10), 0.3)}`;
    
    // Generate CSS sections
    const whitelabelOverrides = isWhitelabel ? generateWhitelabelOverrides(primaryColor, primaryHover, primaryShadow, primaryShadowHover) : '';
    
    const utilityClasses = generateUtilityClasses();
    
    // Combine CSS: variables + whitelabel overrides (if applicable) + utility classes (always)
    styleTag.textContent = `:root {\n${cssVariables}\n}\n${whitelabelOverrides}${utilityClasses}`;
    
    // Inject into <head> - SINGLE DOM operation
    document.head.appendChild(styleTag);
    
    // Save reference and persist
    this.currentTheme = theme;
    localStorage.setItem(this.storageKey, JSON.stringify(theme));
  }

  getVariable(varName) {
    const root = document.documentElement;
    const name = varName.startsWith('--') ? varName : `--${varName}`;
    return getComputedStyle(root).getPropertyValue(name).trim();
  }

  setVariable(varName, value) {
    const root = document.documentElement;
    const name = varName.startsWith('--') ? varName : `--${varName}`;
    root.style.setProperty(name, value);
  }

  resetTheme() {
    // Remove style tag
    const styleTag = document.getElementById(this.styleTagId);
    if (styleTag) {
      styleTag.remove();
    }
    
    // Clear storage
    this.currentTheme = null;
    localStorage.removeItem(this.storageKey);
    
    // Apply default theme (not whitelabel)
    try {
      const defaultTheme = generateFullThemeFromUserInput({
        primary: '#7367f0',
        textOnPrimary: null,
        headerBg: 'linear-gradient(120deg ,#6947bc, #993e90)'
      });
      this.applyTheme(defaultTheme, false); // false = not whitelabel
    } catch (e) {
      console.warn('Could not restore default theme after reset:', e);
    }
  }
}
